import React from "react";
import { Truck, CheckCircle2, AlertTriangle, ShieldCheck, Wrench } from "lucide-react";
import { Loading, ErrorState } from "../ui/primitives.jsx";
import { fmtMoney, fmtDate, todayStr } from "../lib/format.js";
import { fmtTime, addDays, rentalLiveStatus, lateInfo, useNow } from "../lib/rental-time.js";
import { equipmentLiveStatus } from "../equipment/api.js";
import { useDashboardData } from "./api.js";

function ActionList({ items, icon: Icon, empty, render }) {
  if (items.length === 0) {
    return empty ? (
      <div style={{ color: "var(--muted)", fontSize: 13, padding: "6px 0" }}>{empty}</div>
    ) : null;
  }
  return items.map((it, i) => (
    <div key={i} className="attrow">
      <Icon size={16} style={{ color: "var(--orange-dk)", flexShrink: 0 }} />
      {render(it)}
    </div>
  ));
}

export default function Dashboard({ onGoToTab }) {
  const { rentals, equipment, customers, isPending, error, refetch } = useDashboardData();
  useNow(); // keeps the overdue counters advancing without a reload

  if (isPending) return <div className="card"><div className="card-b"><Loading what="the dashboard" /></div></div>;
  if (error) {
    return (
      <div className="card">
        <div className="card-b"><ErrorState error={error} onRetry={refetch} /></div>
      </div>
    );
  }

  const t = todayStr();
  const rs = rentals.data;
  const eq = equipment.data;
  const cs = customers.data;

  const activeEq = eq.filter((e) => e.status !== "retired");
  const onRent = activeEq.filter((e) => equipmentLiveStatus(e, rs) === "out").length;
  const utilization = activeEq.length ? Math.round((onRent / activeEq.length) * 100) : 0;

  const mStart = t.slice(0, 7) + "-01";
  const revenueMTD = rs
    .filter((r) => r.status !== "cancelled" && r.paid && (r.paid_date || r.start_date) >= mStart)
    .reduce((s, r) => s + (Number(r.total) || 0), 0);

  const unpaid = rs.filter((r) => r.status !== "cancelled" && !r.paid);
  const outstanding = unpaid.reduce((s, r) => s + (Number(r.total) || 0), 0);

  const pickupsToday = rs.filter((r) => r.status === "reserved" && r.start_date === t);
  const returnsToday = rs.filter((r) => r.status === "out" && r.end_date === t);
  const overdue = rs.filter((r) => rentalLiveStatus(r) === "overdue");
  const coiExpiring = cs.filter((c) => c.coi_expires && c.coi_expires <= addDays(t, 30));
  const maintDue = eq.filter(
    (e) => e.next_service_date && e.next_service_date <= addDays(t, 14) && e.status !== "retired"
  );

  // v10 has no state for a reservation whose pickup date has passed: it is not
  // overdue (nothing left the yard) but it is not fine either. Surfaced here so
  // a no-show stops being invisible.
  const awaitingPickup = rs.filter((r) => r.status === "reserved" && r.start_date < t);

  const eqName = (r) => r.equipment?.name ?? "—";
  const custName = (r) => r.customers?.name ?? "—";

  return (
    <>
      <div className="stat-row">
        <div className="stat">
          <div className="tag">Units on rent</div>
          <div className="num">
            {onRent}
            <span style={{ fontSize: 18, color: "var(--muted)" }}> / {activeEq.length}</span>
          </div>
          <div className="sub">{utilization}% fleet utilization</div>
        </div>
        <div className="stat money">
          <div className="tag">Collected this month</div>
          <div className="num">{fmtMoney(revenueMTD)}</div>
          <div className="sub">paid rentals, month to date</div>
        </div>
        <div className={"stat" + (outstanding > 0 ? " warn" : " ok")}>
          <div className="tag">Unpaid balance</div>
          <div className="num">{fmtMoney(outstanding)}</div>
          <div className="sub">{unpaid.length} open invoice{unpaid.length === 1 ? "" : "s"}</div>
        </div>
        <div className={"stat" + (overdue.length > 0 ? " warn" : " ok")}>
          <div className="tag">Overdue returns</div>
          <div className="num">{overdue.length}</div>
          <div className="sub">past scheduled return date</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(310px,1fr))", gap: 18 }}>
        <div className="card">
          <div className="card-h">
            <h2>Today's moves</h2>
            <div className="spacer" />
            <button className="btn sm ghost" onClick={() => onGoToTab?.("calendar")}>Calendar →</button>
          </div>
          <div className="card-b">
            <div className="tag-eyebrow" style={{ marginBottom: 4 }}>Going out</div>
            <ActionList
              items={pickupsToday}
              icon={Truck}
              empty="No pickups or deliveries scheduled today."
              render={(r) => (
                <div>
                  <b>{eqName(r)}</b> → {custName(r)}{" "}
                  <span className="sub-cell">(through {fmtDate(r.end_date)})</span>
                </div>
              )}
            />
            <div className="tag-eyebrow" style={{ margin: "12px 0 4px" }}>Coming back</div>
            <ActionList
              items={returnsToday}
              icon={CheckCircle2}
              empty="No returns due today."
              render={(r) => <div><b>{eqName(r)}</b> from {custName(r)}</div>}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-h"><h2>Needs attention</h2></div>
          <div className="card-b">
            <ActionList
              items={overdue}
              icon={AlertTriangle}
              render={(r) => {
                const li = lateInfo(r);
                return (
                  <div>
                    <b>{eqName(r)}</b> is overdue by{" "}
                    <b style={{ color: "var(--red)" }}>{li ? li.label : "—"}</b> — was due back{" "}
                    {fmtDate(r.end_date)} {fmtTime(r.end_time)} from {custName(r)}
                  </div>
                );
              }}
            />
            <ActionList
              items={awaitingPickup}
              icon={Truck}
              render={(r) => (
                <div>
                  <b>{eqName(r)}</b> was reserved for {custName(r)} from {fmtDate(r.start_date)} and
                  has not gone out yet
                </div>
              )}
            />
            <ActionList
              items={coiExpiring}
              icon={ShieldCheck}
              render={(c) => (
                <div>Insurance certificate for <b>{c.name}</b> expires {fmtDate(c.coi_expires)}</div>
              )}
            />
            <ActionList
              items={maintDue}
              icon={Wrench}
              render={(e) => <div><b>{e.name}</b> — service due {fmtDate(e.next_service_date)}</div>}
            />
            {overdue.length + awaitingPickup.length + coiExpiring.length + maintDue.length === 0 && (
              <div style={{ color: "var(--green)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                <CheckCircle2 size={17} /> All clear. Nothing needs attention right now.
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-h">
            <h2>Unpaid rentals</h2>
            <div className="spacer" />
            <button className="btn sm ghost" onClick={() => onGoToTab?.("rentals")}>All rentals →</button>
          </div>
          <div className="card-b tight">
            {unpaid.length === 0 ? (
              <div className="empty" style={{ padding: 20 }}>Everything's collected.</div>
            ) : (
              <table className="tbl">
                <tbody>
                  {unpaid.slice(0, 6).map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div className="main-cell">{custName(r)}</div>
                        <div className="sub-cell">{eqName(r)}</div>
                      </td>
                      <td>{fmtMoney(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
